import { usePostAuthResetPasswordMutation } from '@/api/serverApi'
import { BrandBackground, Button, InputLabel, PadlockIcon, TextInput, UserIcon, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as v from 'valibot'

const resetPasswordFormSchema = v.object({
  token: v.string(),
  email: v.string([v.email()]),
  password: v.string([v.minLength(1, 'Password is required')]),
  password_confirmation: v.string([v.minLength(1, 'Password confirmation is required')]),
})

type ResetPasswordFormInputs = v.Input<typeof resetPasswordFormSchema>

const textSpec = {
  title: 'Reset Your Password',
  subtitle: 'Enter your new password below',
  passwordField: {
    label: 'PASSWORD',
    placeholder: 'Password',
  },
  passwordConfirmationField: {
    label: 'CONFIRM PASSWORD',
    placeholder: 'Password',
  },
  resetPasswordButton: 'Reset Password',
  successToastMessage: {
    title: 'Password reset successfully',
    body: 'Your password has now been changed, please login with your new password.',
  },
  errorToastMessage: {
    title: 'Something went wrong',
    body: 'Please try again later.',
  },
}

function ResetPasswordForm () {
  const errorHandler = useErrorHandler()

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const { register, handleSubmit, formState: { errors }, setError, watch } = useForm<ResetPasswordFormInputs>({
    defaultValues: {
      token: searchParams.get('token') ?? '',
      email: searchParams.get('email') ?? '',
      password: '',
      password_confirmation: '',
    },
    resolver: valibotResolver(resetPasswordFormSchema),
  })

  const email = watch('email')

  const [resetPassword, { isLoading: isSubmitting }] = usePostAuthResetPasswordMutation()

  const onSubmit: SubmitHandler<ResetPasswordFormInputs> = (data) => {
    resetPassword({ body: data })
      .unwrap()
      .then((_) => {
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        navigate('/')
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-center w-full max-w-[380px]"
    >
      <input
        {...register('token')}
        type="hidden"
      />
      <input
        {...register('email')}
        type="hidden"
      />
      <BrandBackground className="w-[50px] h-[50px]">
        <PadlockIcon className="h-[32px] w-[32px] text-grey-800" />
      </BrandBackground>

      <h1 className="mb-[15px] mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.title}</h1>

      <p className="mb-[50px] text-base leading-[22.4px] text-center">{textSpec.subtitle}</p>

      <div className="mb-7 w-full flex flex-row items-center justify-start gap-[5px]">
        <UserIcon className="h-[18px] w-[18px]" />
        <p className="text-base leading-[22.4px] text-center font-bold">{email}</p>
      </div>

      <div className="mb-[30px] w-full">
        <InputLabel name="password">{textSpec.passwordField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.passwordField.placeholder}
          type="password"
          error={errors?.password?.message}
          {...register('password')}
        />
      </div>

      <div className="mb-10 w-full">
        <InputLabel name="password_confirmation">{textSpec.passwordConfirmationField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.passwordConfirmationField.placeholder}
          type="password"
          error={errors?.password_confirmation?.message}
          {...register('password_confirmation')}
        />
      </div>

      <div className="mt-2 w-full">
        <Button
          variant="dark"
          isLoading={isSubmitting}
          type="submit"
          isBlock
        >
          {textSpec.resetPasswordButton}
        </Button>
      </div>
    </form>
  )
}

export default ResetPasswordForm
